import { CommonModule } from '@angular/common';
import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { Modal, ModalOptions, ModalService, ToastType } from '@indice/ng-components';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { User } from 'oidc-client-ts';
import { tap } from 'rxjs';
import { AuthService } from 'src/app/core/services/auth.service';
import { Photo, PhotoResultSet, PropertyDetails } from 'src/app/services/portal-api.service';
import { ToasterService } from 'src/app/services/toaster.service';
import { RequestCallbackModalComponent } from 'src/app/shared/components/request-callback-modal/request-callback-modal.component';
import { RequestWalkthroughModalComponent } from 'src/app/shared/components/request-walkthrough-modal/request-walkthrough-modal.coponent';
import { IAsideModalResponse } from '../../aside-content/aside-content.component';

@Component({
  selector: 'app-modal-image-gallery',
  standalone: true,
  templateUrl: './modal-image-gallery.component.html',
  imports: [CommonModule, TranslocoDirective,],
})
export class ModalImageGalleryComponent {

  public title: string = '';
  public countPhotos: number = 0;
  public photos: PhotoResultSet | undefined;
  public isSold: boolean = false;
  protected displyedItems: Photo[] = [];
  protected selfHidden: boolean = false;
  protected currentPhotoIndex: number = 0;
  protected displayingImage: boolean = false;
  private currentUser: User | null;
  public property: PropertyDetails | undefined;
  private toastTranslations:any;

  constructor(
    public modal: Modal,
    @Inject(ModalOptions)
    private options: ModalOptions<ModalImageGalleryComponent>,
    private _modalService: ModalService,
    private _authService: AuthService,
    private _translate : TranslocoService,
    @Inject(ToasterService) private _toastService: ToasterService
  ) {
    this.title = options?.initialState?.title as string;
    this.countPhotos = options?.initialState?.countPhotos as number;
    this.photos = options?.initialState?.photos as PhotoResultSet;
    this.property = options?.initialState?.property as PropertyDetails;
    this.isSold = options?.initialState?.isSold as boolean || false;
    this.currentUser = this._authService.getCurrentUser();
    this._translate.selectTranslateObject('toast-workflows-messages').pipe(
      tap((translations) => {
        this.toastTranslations = translations;
      })
    ).subscribe();

    this.displyedItems = this.getDisplayedItems();
  }

  private getDisplayedItems(): Photo[] {
    const displayItems: Photo[] = [];
    const photosLength = this.photos?.items?.length ?? 0;
    for (let i = 0; i < photosLength; i++) {
      let photo: Photo | undefined = this.photos?.items?.[i];
      if (!photo) continue;
      if (Array.isArray(photo.photoItems)) {
        // Find the photoItem with URI ending in -s.webp
        const smallWebpUri = photo.photoItems?.find(f => f?.uri?.endsWith('-m.webp'))?.uri;

        if (smallWebpUri) {
          // Replace the uri with the small webp version
          photo = new Photo({
            ...photo,
            uri: smallWebpUri
          });
        }
      }
      displayItems.push(photo);
    }

    return displayItems;
  }

  protected getLargeImageUri(photoIndex: number): string {
    const photo = this.photos?.items?.[photoIndex];
    if (!photo) return '';

    if (!Array.isArray(photo.photoItems)) {
      return photo.uri || '';
    }

    let webpUri = photo.photoItems.find(f => f?.uri?.endsWith('-l.webp'))?.uri;

    return webpUri || photo.uri || '';
  }
  closeModal() {
    this.modal.hide({});
  }

  protected onAskTourClick(): void {
    this.closeModal();
    const modal = this._modalService.show(RequestWalkthroughModalComponent, {
      keyboard: true,
      class: 'h-dvh md:h-auto',
      initialState: {
        property: this.property,
        isLoggedIn: this.currentUser !== null,
      },
    });
    modal.onHidden?.pipe(
      tap((r: any) => {
        let input = r.result as IAsideModalResponse;
        if (input.success != undefined) {
          if (input.success == true) {
            this._toastService.showBanner(ToastType.Success, this.toastTranslations?.walkthrough?.success?.title ?? '', undefined, 10000);
          } else {
            this._toastService.showBanner(ToastType.Error, this.toastTranslations?.walkthrough?.error?.title ?? '', input.errorDesc, 10000);
          }
        }
      })
    ).subscribe();
  }

  public onCallbackClick() {
      this.closeModal();
      const modal = this._modalService.show(RequestCallbackModalComponent, {
        keyboard: true,
        class: 'h-dvh md:h-auto',
        initialState: {
          property: this.property,
          isLoggedIn: this.currentUser !== null,
        },
      });
      modal.onHidden?.pipe(
        tap((t: any) => {
          let input = t.result as IAsideModalResponse;
          if (input.success != undefined) {
            if (input.success == true) {
              this._toastService.showBanner(ToastType.Success,this.toastTranslations?.callback?.success?.title ?? '', 
              (this.toastTranslations?.callback?.success?.title  ?? '' as string)
              .replace('{{fullName}}', this.property?.brokerName ?? '') 
              .replace('{{phoneNumber}}', this.currentUser?.profile?.phone_number ?? '')
            )
            } else {
              this._toastService.showBanner(ToastType.Error, this.toastTranslations?.callback?.error?.title ?? '', input.errorDesc, 10000);
            }
          }
        })
      ).subscribe();
    }

  /*When a child modal is created, this function hides this modal*/
  selfHideSwitch(event: boolean) {
    this.selfHidden = event;
  }

  viewCarousel(index: number) {
    this.currentPhotoIndex = index;
    this.displayingImage = true;
  }

  exitCarousel() {
    this.currentPhotoIndex = 0;
    this.displayingImage = false;
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'ArrowRight') {
      this.next();
    } else if (event.key === 'ArrowLeft') {
      this.previous();
    }
  }

  next() {
    /**
     * if we need continous navigation: 
     * if (this.photos && this.photos.count) {
     *   this.currentPhotoIndex = (this.currentPhotoIndex + 1) % this.photos.count;
     * }
     */
    if (this.currentPhotoIndex === (this.photos && this.photos.count && this.photos?.count - 1)) {
      return;
    }
    this.currentPhotoIndex += 1;
  }

  previous() {
    /**
     * if we need continous navigation: 
     *  if (this.photos && this.photos.count) {
     *    this.currentPhotoIndex = (this.currentPhotoIndex - 1 + this.photos.count) % this.photos.count;
     *  }
     */
    if (this.currentPhotoIndex === 0) {
      return;
    }
    this.currentPhotoIndex -= 1;
  }
}
